/**
 * M1 nav baseline recorder (opt-in).
 *
 * Enable (logged-in browser console, once per session):
 *   localStorage.setItem('bb-perf-nav', '1'); location.reload();
 *
 * Then cold-open each warm-target route (full reload or from login),
 * navigate away, warm-open again (client nav). Dump:
 *   copy(JSON.stringify(window.__bbPerfNav.rows(), null, 2))
 *
 * Disable:
 *   localStorage.removeItem('bb-perf-nav'); location.reload();
 *
 * Rows land in localStorage key `bb-perf-nav-rows` and on window.__bbPerfNav.
 */

export type NavBaselineRow = {
  route: string;
  kind: 'cold' | 'warm' | 'unknown';
  /** ms from navigation start → first contentful paint-ish (see note) */
  ms: number;
  at: string;
  fullSkeleton: boolean | null;
  note?: string;
};

const FLAG = 'bb-perf-nav';
const STORE = 'bb-perf-nav-rows';

const WARM_TARGETS = [
  '/staff/inventory',
  '/staff/requests',
  '/staff/students',
  '/student/dashboard',
  '/student/browse',
  '/student/cart',
] as const;

export function isPerfNavEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

export function readNavBaselineRows(): NavBaselineRow[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRows(rows: NavBaselineRow[]) {
  localStorage.setItem(STORE, JSON.stringify(rows));
}

function classify(route: string, priorForRoute: number): NavBaselineRow['kind'] {
  if (!WARM_TARGETS.some((t) => route === t || route.startsWith(t + '/'))) return 'unknown';
  return priorForRoute === 0 ? 'cold' : 'warm';
}

/**
 * Record one sample. `ms` should be click→content-visible when possible.
 * Probe uses PerformanceNavigationTiming / paint when available.
 */
export function recordNavBaseline(partial: {
  route: string;
  ms: number;
  kind?: NavBaselineRow['kind'];
  fullSkeleton?: boolean | null;
  note?: string;
}) {
  const existing = readNavBaselineRows();
  const prior = existing.filter((r) => r.route === partial.route).length;
  const row: NavBaselineRow = {
    route: partial.route,
    kind: partial.kind ?? classify(partial.route, prior),
    ms: Math.round(partial.ms),
    at: new Date().toISOString(),
    fullSkeleton: partial.fullSkeleton ?? null,
    note: partial.note,
  };
  const next = [...existing, row];
  writeRows(next);
  return row;
}

export function measurePaintSince(startMs: number): number {
  const now = performance.now();
  return Math.max(0, now - startMs);
}

declare global {
  interface Window {
    __bbPerfNav?: {
      enabled: () => boolean;
      rows: () => NavBaselineRow[];
      record: typeof recordNavBaseline;
      clear: () => void;
      targets: readonly string[];
    };
  }
}

export function installNavBaselineGlobals() {
  if (typeof window === 'undefined') return;
  window.__bbPerfNav = {
    enabled: isPerfNavEnabled,
    rows: readNavBaselineRows,
    record: recordNavBaseline,
    clear: () => localStorage.removeItem(STORE),
    targets: WARM_TARGETS,
  };
}
